/**
 * Fakturadialogens kilde-til-felt-regler og oversettelse av PowerOffice sine
 * send-feil. Rene funksjoner, testet i `__tests__/invoice-send.test.ts`.
 *
 * Prinsipp (Erik, 23.10.2026): feltene i dialogen hentes fra prosjektkortet,
 * og faller tilbake på kundekortet. Prosjektkortet overstyrer kundekortet.
 * Edge-funksjonen `create-invoice` bruker samme kjede server-side, så det
 * dialogen viser er det PO får.
 */

export type InvoiceFieldSource = "project" | "customer" | "none";

export interface InvoiceDefaultsProject {
  deres_ref: string | null;
  deres_ref_kode: string | null;
  customer_reference_person: string | null;
  customer_reference_number: string | null;
  invoice_reference: string | null;
  payment_due_days: number | null;
  invoice_email: string | null;
}

export interface InvoiceDefaultsCustomer {
  contact_person: string | null;
  invoice_email_address: string | null;
}

export interface ResolvedInvoiceField<T> {
  value: T | null;
  source: InvoiceFieldSource;
}

export interface InvoiceDefaults {
  customerReference: ResolvedInvoiceField<string>;
  purchaseOrderReference: ResolvedInvoiceField<string>;
  paymentDueDays: ResolvedInvoiceField<number>;
  deliveryEmail: ResolvedInvoiceField<string>;
}

function text(v: string | null | undefined): string | null {
  const t = v?.trim();
  return t ? t : null;
}

function pick<T>(
  candidates: Array<[T | null, InvoiceFieldSource]>,
): ResolvedInvoiceField<T> {
  for (const [value, source] of candidates) {
    if (value != null) return { value, source };
  }
  return { value: null, source: "none" };
}

/**
 * Prosjekt > kunde for hvert felt. `deres_ref`/`deres_ref_kode` er kanoniske (VIB-1191).
 * VIB-3030: prosjektets felt er hovedkundens. Går fakturaen til en annen kunde
 * (`isMainCustomer = false`), brukes bare den kundens kundekort.
 */
export function resolveInvoiceDefaults(
  project: InvoiceDefaultsProject,
  customer: InvoiceDefaultsCustomer | null,
  isMainCustomer = true,
): InvoiceDefaults {
  if (!isMainCustomer) {
    return {
      customerReference: pick<string>([[text(customer?.contact_person), "customer"]]),
      purchaseOrderReference: pick<string>([]),
      paymentDueDays: pick<number>([]),
      deliveryEmail: pick<string>([[text(customer?.invoice_email_address), "customer"]]),
    };
  }
  return {
    customerReference: pick<string>([
      [text(project.deres_ref) ?? text(project.customer_reference_person), "project"],
      [text(customer?.contact_person), "customer"],
    ]),
    purchaseOrderReference: pick<string>([
      [
        text(project.deres_ref_kode) ??
          text(project.customer_reference_number) ??
          text(project.invoice_reference),
        "project",
      ],
    ]),
    paymentDueDays: pick<number>([[project.payment_due_days ?? null, "project"]]),
    deliveryEmail: pick<string>([
      [text(project.invoice_email), "project"],
      [text(customer?.invoice_email_address), "customer"],
    ]),
  };
}

export type InvoiceFieldKey = keyof InvoiceDefaults;

export interface InvoiceFieldIssue {
  field: InvoiceFieldKey;
  /** `error` blokkerer sending, `warning` er en påminnelse. */
  severity: "error" | "warning";
  message: string;
}

export interface InvoiceFieldValues {
  customerReference: string;
  purchaseOrderReference: string;
  paymentDueDays: number | "";
  deliveryEmail: string;
}

export interface InvoiceSendContext {
  sendNow: boolean;
  deliveryType: string;
  /** VIB-3030: false når fakturaen går til en annen kunde. Da gjelder ikke prosjektets felt. */
  isMainCustomer?: boolean;
}

export const INVOICE_EMAIL_HINT =
  "Legg inn «Faktura-e-post» på kundekortet (Detaljer → Kunde), eller sett «Fakturamail» på prosjektet (Innstillinger → Fakturering).";

/** VIB-3030: for en annen kunde enn hovedkunden gjelder bare kundekortet. */
const OTHER_CUSTOMER_EMAIL_HINT = "Skriv inn e-post her, eller legg inn «Faktura-e-post» på kundekortet til kunden.";

/** Hva som mangler i dialogen, og om det stopper sending. */
export function invoiceFieldIssues(
  values: InvoiceFieldValues,
  ctx: InvoiceSendContext,
): InvoiceFieldIssue[] {
  const issues: InvoiceFieldIssue[] = [];
  const main = ctx.isMainCustomer ?? true;
  const emailHint = main ? INVOICE_EMAIL_HINT : OTHER_CUSTOMER_EMAIL_HINT;
  const email = values.deliveryEmail.trim();
  if (ctx.sendNow && !email) {
    if (ctx.deliveryType === "PdfByEmail") {
      issues.push({
        field: "deliveryEmail",
        severity: "error",
        message: `Fakturaen kan ikke sendes på e-post uten adresse. ${emailHint}`,
      });
    } else if (ctx.deliveryType === "Auto") {
      issues.push({
        field: "deliveryEmail",
        severity: "warning",
        message: `Ingen faktura-e-post er kjent. «Auto» virker bare hvis kunden er EHF-registrert, ellers blir fakturaen liggende som utkast i Go. ${emailHint}`,
      });
    }
  }
  if (!values.customerReference.trim()) {
    issues.push({
      field: "customerReference",
      severity: "warning",
      message: main
        ? "Deres ref. mangler. Sett «Deres ref.» på prosjektet (Innstillinger → Fakturering) eller kontaktperson på kundekortet."
        : "Deres ref. mangler. Skriv den inn her, eller legg inn kontaktperson på kundekortet til kunden.",
    });
  }
  if (!values.purchaseOrderReference.trim()) {
    issues.push({
      field: "purchaseOrderReference",
      severity: "warning",
      message: "Deres ref. kode mangler. Trengs når kunden krever PO-/referansenummer på fakturaen.",
    });
  }
  if (values.paymentDueDays === "") {
    issues.push({
      field: "paymentDueDays",
      severity: "warning",
      message: "Betalingsfrist mangler. PowerOffice bruker da kundens standard.",
    });
  }
  return issues;
}

export function hasBlockingIssue(issues: InvoiceFieldIssue[]): boolean {
  return issues.some((i) => i.severity === "error");
}

/** Samme koder som `create-invoice/po-error.ts` (kan ikke importeres på tvers av runtime). */
export type PoSendErrorCode = "MISSING_INVOICE_EMAIL" | "NOT_EHF_REGISTERED" | "PO_SEND_FAILED";

export interface ParsedPoSendError {
  code: PoSendErrorCode;
  detail: string;
}

/** Trekker ut PO sin `detail` fra den rå feilteksten lagret i `invoices.last_send_error`. */
export function parsePoSendError(raw: string): ParsedPoSendError {
  let detail = raw;
  const jsonStart = raw.indexOf("{");
  if (jsonStart >= 0) {
    try {
      const parsed = JSON.parse(raw.slice(jsonStart)) as Record<string, unknown>;
      const candidate =
        (typeof parsed.detail === "string" && parsed.detail) ||
        (typeof parsed.title === "string" && parsed.title) ||
        null;
      if (candidate) detail = candidate;
    } catch {
      // ikke JSON
    }
  }
  const d = detail.toLowerCase();
  const code: PoSendErrorCode =
    d.includes("email is not provided") || d.includes("invoice email")
      ? "MISSING_INVOICE_EMAIL"
      : d.includes("ehf")
        ? "NOT_EHF_REGISTERED"
        : "PO_SEND_FAILED";
  return { code, detail };
}

export interface SendFailureText {
  title: string;
  explanation: string;
  action: string;
}

/** Klartekst for en send-feil: hva skjedde, hvorfor, og hva brukeren må gjøre. */
export function describeSendFailure(err: { code?: string | null; detail?: string | null }): SendFailureText {
  const detail = err.detail?.trim() || "PowerOffice ga ingen begrunnelse.";
  switch (err.code) {
    case "MISSING_INVOICE_EMAIL":
      return {
        title: "Fakturaen ble opprettet, men ikke sendt: mangler e-postadresse",
        explanation:
          "PowerOffice har ingen adresse å sende til. Fakturaen ligger som utkast i Go.",
        action: `${INVOICE_EMAIL_HINT} Send deretter utkastet fra Go, eller lag fakturaen på nytt.`,
      };
    case "NOT_EHF_REGISTERED":
      return {
        title: "Fakturaen ble opprettet, men ikke sendt: kunden er ikke EHF-registrert",
        explanation: `PowerOffice svarte: ${detail}`,
        action: "Velg «PDF på epost» som leveringsmåte, og sørg for at kunden har faktura-e-post.",
      };
    default:
      return {
        title: "Fakturaen ble opprettet, men ikke sendt",
        explanation: `PowerOffice svarte: ${detail}`,
        action: "Fakturaen ligger som utkast i Go. Rett årsaken og send den derfra, eller lag fakturaen på nytt.",
      };
  }
}
